import { X } from "lucide-react";

import { platform } from "~/lib/platform";
import { IconTooltip } from "~/components/IconTooltip";
import { ICON_STROKE } from "~/lib/icon";

export interface PermissionBannerProps {
  /** Hidden / visible. The hook decides; the component only renders. */
  visible: boolean;
  /** Firefox and Safari require notification permission requests to run inside this user gesture. */
  onEnable: () => void;
  onDismiss: () => void;
  /** The browser already refused; only its site settings can allow notifications again. */
  blocked?: boolean;
  /**
   * Optional learn-more link. Defaults to the browser's / OS's
   * system-settings hint; callers may pass a docs URL when one
   * exists.
   */
  learnMoreHref?: string;
}

export function PermissionBanner({
  visible,
  onEnable,
  onDismiss,
  learnMoreHref,
  blocked = false,
}: PermissionBannerProps) {
  if (!visible) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        height: "var(--chat-space-10)",
        padding: "0 var(--chat-space-4)",
        background: "var(--chat-surface-raised)",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        {blocked
          ? platform === "tauri"
            ? "Notifications are blocked. Allow them for Nurion Chat in System Settings."
            : "Notifications are blocked. Allow them for this site in your browser settings."
          : "Enable notifications to get pinged for DMs and mentions."}{" "}
        {learnMoreHref && (
          <a
            href={learnMoreHref}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "var(--chat-accent)", textDecoration: "none" }}
          >
            Learn more
          </a>
        )}
      </span>
      {!blocked && (
        <button
          type="button"
          onClick={onEnable}
          aria-label="Enable notifications"
          className="chat-button"
          style={{
            height: "var(--chat-space-6)",
            padding: "0 var(--chat-space-3)",
            border: "none",
            background: "var(--chat-cta-bg)",
            color: "var(--chat-cta-fg)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
            fontWeight: 500,
            borderRadius: "var(--chat-radius-sm)",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          Enable
        </button>
      )}
      <IconTooltip label="Dismiss notifications banner">
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notifications banner"
          className="chat-button"
          style={{
            width: "var(--chat-space-6)",
            height: "var(--chat-space-6)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            background: "transparent",
            color: "var(--chat-fg-secondary)",
            borderRadius: "var(--chat-radius-sm)",
            cursor: "pointer",
            padding: 0,
            flexShrink: 0,
          }}
        >
          <X size={16} strokeWidth={ICON_STROKE} aria-hidden />
        </button>
      </IconTooltip>
    </div>
  );
}
